import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Settings2, Link2, Upload } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";
import ScheduleCalendar, { WEEKDAY_PILLS, repeatDaysLabel } from "@/components/ScheduleCalendar";
import { formatDateOnly } from "@/lib/date";
import CalendarMenu from "@/components/CalendarMenu";
import BulkImportDialog from "@/components/BulkImportDialog";
import ImportHealthBadge from "@/components/ImportHealthBadge";
import GoogleCalendarCard from "@/components/GoogleCalendarCard";
import AttendanceSheetDialog from "@/components/AttendanceSheetDialog";
import { useFeatureFlags, FeatureDisabled } from "@/lib/featureFlags";
import ScheduleTabs, { useTabParam } from "@/components/ScheduleTabs";
import CoachVolunteers from "@/pages/coach/Volunteers";
import CoachAttendance from "@/pages/coach/AttendanceGrid";
import CoachConflicts from "@/pages/coach/Conflicts";

const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Phoenix", label: "Arizona (MST, no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HST)" },
];
const DEFAULT_TZ = "America/Chicago";

const EMPTY = { title: "", event_date: "", end_date: "", recurrence: "none", silent: false, start_time: "", end_time: "", all_day: false, timezone: DEFAULT_TZ, location: "", description: "", subgroup_ids: [], repeat_days: [] };

const RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom (pick days)" },
];

const parseYmd = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const weekdayOf = (s) => (parseYmd(s).getDay() + 6) % 7;

// How many dates between start and end fall on the chosen weekdays (Mon=0 … Sun=6). Stops just past 400.
function countCustomDates(start, end, days) {
  if (!start || !end || end < start || !days.length) return 0;
  let n = 0;
  for (let d = parseYmd(start), last = parseYmd(end); d <= last && n <= 400; d.setDate(d.getDate() + 1)) {
    if (days.includes((d.getDay() + 6) % 7)) n += 1;
  }
  return n;
}

function EventDialog({ open, setOpen, editing, onDone, defaultDate }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [subgroups, setSubgroups] = useState([]);
  const [teamTotal, setTeamTotal] = useState(null);
  const [editScope, setEditScope] = useState("one");

  useEffect(() => {
    if (open) api.get("/coach/subgroups").then(({ data }) => { setSubgroups(data.items || []); setTeamTotal(data.team_total ?? null); }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (editing) setForm({ title: editing.title || "", event_date: (editing.event_date || "").slice(0, 10), end_date: "", recurrence: "none", start_time: editing.start_time || "", end_time: editing.end_time || "", all_day: !!editing.all_day, timezone: editing.timezone || DEFAULT_TZ, location: editing.location || "", description: editing.description || "", subgroup_ids: editing.subgroup_ids || [], repeat_days: [] });
    else setForm({ ...EMPTY, event_date: defaultDate || "" });
    setEditScope("one");
  }, [editing, open, defaultDate]);

  const repeats = form.recurrence && form.recurrence !== "none";
  const isCustom = form.recurrence === "custom";
  const customCount = isCustom ? countCustomDates(form.event_date, form.end_date, form.repeat_days) : 0;
  const toggleDay = (v) => setForm((f) => ({ ...f, repeat_days: f.repeat_days.includes(v) ? f.repeat_days.filter((x) => x !== v) : [...f.repeat_days, v] }));
  const editingRecurring = !!(editing && editing.recurrence);

  const submit = async () => {
    if (!form.title.trim() || !form.event_date) { toast.error("Title and date are required"); return; }
    if (repeats && !form.end_date) { toast.error("Set an end date for the repeating series"); return; }
    if (form.end_date && form.end_date < form.event_date) { toast.error("End date must be on or after the start date"); return; }
    if (isCustom && !form.repeat_days.length) { toast.error("Pick at least one day of the week"); return; }
    if (isCustom && customCount === 0) { toast.error("None of the selected days fall within that date range"); return; }
    if (isCustom && customCount > 400) { toast.error("That would create more than 400 events — shorten the date range"); return; }
    if (!form.all_day && form.start_time && form.end_time && form.end_time < form.start_time) { toast.error("End time must be after the start time"); return; }
    setLoading(true);
    try {
      const payload = { ...form, all_day: form.all_day };
      if (form.all_day) { payload.start_time = ""; payload.end_time = ""; }
      if (editing || !isCustom) delete payload.repeat_days;
      if (editing) { delete payload.end_date; delete payload.recurrence; delete payload.silent; }
      const groups = payload.subgroup_ids || [];
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      payload.subgroup_ids = groups;
      if (editing) {
        if (editingRecurring && editScope !== "one") {
          delete payload.event_date;
          const { data } = await api.put(`/coach/events/${editing.id}/series`, { ...payload, scope: editScope });
          toast.success(`Updated ${data.updated} occurrence${data.updated === 1 ? "" : "s"}`);
        } else {
          await api.put(`/coach/events/${editing.id}`, payload);
          toast.success("Event updated");
        }
      }
      else { const { data } = await api.post("/coach/events", payload); const base = data.days_created > 1 ? `Event created for ${data.days_created} ${repeats ? "occurrences" : "days"}` : "Event created"; toast.success(form.silent ? `${base} — saved quietly, no notifications sent` : `${base} & sent to team`); }
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="event-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Team practice" data-testid="event-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>{editing ? "Date" : "Start date"}</Label><Input type="date" value={form.event_date} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, event_date: v, repeat_days: f.recurrence === "custom" && !f.repeat_days.length && v ? [weekdayOf(v)] : f.repeat_days })); }} data-testid="event-date-input" /></div>
            {!editing && <div className="space-y-2"><Label>{repeats ? "Repeat until" : "End date"} <span className="text-slate-400">{repeats ? "" : "(optional)"}</span></Label><Input type="date" min={form.event_date || undefined} value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="event-end-date-input" /></div>}
          </div>
          {!editing && (
            <div className="space-y-2">
              <Label>Repeats</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm((f) => ({ ...f, recurrence: v, repeat_days: v === "custom" && !f.repeat_days.length && f.event_date ? [weekdayOf(f.event_date)] : f.repeat_days }))}>
                <SelectTrigger data-testid="event-recurrence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`event-recurrence-${r.value}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!editing && isCustom && (
            <div className="space-y-2" data-testid="event-repeat-days">
              <Label>Repeat on</Label>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_PILLS.map((p) => {
                  const on = form.repeat_days.includes(p.value);
                  return (
                    <button type="button" key={p.value} onClick={() => toggleDay(p.value)} aria-pressed={on} data-testid={`event-repeat-day-${p.label.toLowerCase()}`}
                      className={`h-9 w-12 rounded-full text-sm font-semibold transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
              {form.repeat_days.length > 0 && form.event_date && form.end_date && form.end_date >= form.event_date && (
                <p className={`text-xs font-semibold ${customCount ? "text-slate-500" : "text-red-600"}`} data-testid="event-custom-preview">
                  {customCount ? `Creates ${customCount > 400 ? "400+" : customCount} event${customCount === 1 ? "" : "s"}: ${repeatDaysLabel(form.repeat_days)} · ${formatDateOnly(form.event_date)} – ${formatDateOnly(form.end_date)}` : "None of the selected days fall within that date range."}
                </p>
              )}
            </div>
          )}
          {!editing && repeats && !isCustom && form.end_date && form.end_date >= form.event_date && (
            <p className="text-xs font-semibold text-slate-500" data-testid="event-recurrence-hint">This event will repeat {form.recurrence} on this day until {form.end_date}.</p>
          )}
          {!editing && !repeats && form.end_date && form.end_date >= form.event_date && (
            <p className="text-xs font-semibold text-slate-500" data-testid="event-range-hint">This event will be added to each day from the start date through the end date.</p>
          )}
          {editingRecurring && (
            <div className="space-y-2 rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
              <Label className="text-indigo-800">This is a repeating event — apply changes to</Label>
              <Select value={editScope} onValueChange={setEditScope}>
                <SelectTrigger data-testid="event-edit-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one" data-testid="event-edit-scope-one">This occurrence only</SelectItem>
                  <SelectItem value="future" data-testid="event-edit-scope-future">This and all upcoming dates</SelectItem>
                  <SelectItem value="all" data-testid="event-edit-scope-all">All dates in the series</SelectItem>
                </SelectContent>
              </Select>
              {editScope !== "one" && <p className="text-xs font-medium text-indigo-600" data-testid="event-edit-scope-hint">The date stays the same for each occurrence — only the details you change here are applied.</p>}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label>Time</Label>
              <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-600">
                <Checkbox checked={form.all_day} onCheckedChange={(v) => setForm((f) => ({ ...f, all_day: !!v, start_time: v ? "" : f.start_time, end_time: v ? "" : f.end_time }))} data-testid="event-all-day-checkbox" />
                All day
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><span className="text-xs text-slate-400">Start</span><Input type="time" value={form.start_time} disabled={form.all_day} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} data-testid="event-time-input" /></div>
              <div className="space-y-1"><span className="text-xs text-slate-400">End (optional)</span><Input type="time" value={form.end_time} disabled={form.all_day} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} data-testid="event-end-time-input" /></div>
            </div>
            <div className="space-y-1">
              <span className="text-xs text-slate-400">Time zone</span>
              <Select value={form.timezone} onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))} disabled={form.all_day}>
                <SelectTrigger data-testid="event-timezone-select"><SelectValue placeholder="Select time zone" /></SelectTrigger>
                <SelectContent>
                  {US_TIMEZONES.map((tz) => <SelectItem key={tz.value} value={tz.value} data-testid={`event-tz-${tz.value}`}>{tz.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="e.g. Main gym" data-testid="event-location-input" /></div>
          <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="event-description-input" /></div>
          {!editing && (
            <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <div className="space-y-0.5">
                <Label htmlFor="silent-mode" className="cursor-pointer">Silent mode</Label>
                <p className="text-xs text-slate-500">Save {form.recurrence !== "none" ? "the series" : "the event"} and sync to Google Calendar, but don't notify the team. Great for bulk-loading the season schedule.</p>
              </div>
              <Switch id="silent-mode" checked={form.silent} onCheckedChange={(v) => setForm((f) => ({ ...f, silent: !!v }))} data-testid="event-silent-toggle" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Send to</Label>
            <div className="flex flex-wrap gap-2" data-testid="event-groups-select">
              <button type="button" onClick={() => setForm((f) => ({ ...f, subgroup_ids: [] }))} data-testid="event-group-all"
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${form.subgroup_ids.length === 0 ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                Entire team{teamTotal != null ? <span className="ml-1 opacity-60">→ {teamTotal}</span> : null}
              </button>
              {subgroups.map((s) => {
                const on = form.subgroup_ids.includes(s.id);
                return (
                  <button type="button" key={s.id} data-testid={`event-group-${s.id}`}
                    onClick={() => setForm((f) => ({ ...f, subgroup_ids: on ? f.subgroup_ids.filter((x) => x !== s.id) : [...f.subgroup_ids, s.id] }))}
                    className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {s.name}<span className="ml-1 opacity-60">→ {s.active_count ?? s.member_count ?? 0}</span>
                  </button>
                );
              })}
            </div>
            {subgroups.length > 0 ? (
              <p className="text-xs text-slate-400">{form.subgroup_ids.length ? "Only members in the selected group(s) will see this event and be notified." : "Visible to the whole team."}</p>
            ) : (
              <p className="text-xs text-slate-400" data-testid="event-groups-empty-hint">This event goes to the entire team. To target specific groups (Freshmen, JV, Varsity, Officers…), first create them on the <Link to="/coach/members?tab=groups" className="font-semibold text-slate-600 underline underline-offset-2 hover:text-slate-900">Groups page</Link>.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="event-submit-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">{editing ? "Save" : "Create & notify"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CalendarTab({ onVolunteer }) {
  const { attendance: attendanceOn } = useFeatureFlags();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [attendEvent, setAttendEvent] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/coach/schedule");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const doDeleteIds = async (ids, msg) => {
    try {
      await Promise.all(ids.map((id) => api.delete(`/coach/events/${id}`)));
      toast.success(msg);
      setDel(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  const upcomingIds = del && del._recurring && del.series_id
    ? items.filter((i) => i.type === "event" && i.series_id === del.series_id && (i.event_date || "") >= (del.event_date || "")).map((i) => i.id)
    : [];

  const doDelete = () => {
    const ids = del._ids && del._ids.length ? del._ids : [del.id];
    return doDeleteIds(ids, "Event deleted");
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Create events for your team — conferences appear automatically.</p>
        <div className="flex flex-wrap items-center gap-2"><ImportHealthBadge onChange={load} /><Button variant="outline" className="rounded-full" onClick={() => setShowSettings((s) => !s)} data-testid="schedule-settings-btn"><Settings2 className="mr-2 h-4 w-4" /> Settings</Button><Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-event-btn"><Plus className="mr-2 h-4 w-4" /> New event</Button></div>
      </div>
      {showSettings && (
        <div className="mb-6 space-y-4" data-testid="schedule-settings-panel">
          <GoogleCalendarCard />
          <Card className="border-slate-200 p-5 shadow-sm" data-testid="calendar-link-card">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <Link2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 font-heading">Link to Another Calendar</p>
                  <p className="text-sm text-slate-500">Subscribe to your RallyPointGo events in Google or Apple Calendar, or copy the feed link.</p>
                </div>
              </div>
              <CalendarMenu onChange={load} canImport label="Link or Import" />
            </div>
          </Card>
          <Card className="border-slate-200 p-5 shadow-sm" data-testid="import-schedule-card">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <Upload className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 font-heading">Import Schedule</p>
                  <p className="text-sm text-slate-500">Bulk-load your season schedule from a CSV — events are added quietly without notifying the team.</p>
                </div>
              </div>
              <BulkImportDialog onDone={load} />
            </div>
          </Card>
        </div>
      )}
      <ScheduleCalendar items={items} canManage onEdit={(it) => { setEditing(it); setOpen(true); }} onDelete={(it) => setDel(it)} onSelectDate={setSelectedDate} showAttendance={attendanceOn} onAttendance={(it) => setAttendEvent(it)} onVolunteer={onVolunteer} />
      {attendEvent && <AttendanceSheetDialog event={attendEvent} open onClose={() => setAttendEvent(null)} onSaved={load} />}
      <EventDialog open={open} setOpen={setOpen} editing={editing} onDone={load} defaultDate={selectedDate} />
      <Dialog open={!!del && !!del._recurring} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="delete-recurring-dialog">
          <DialogHeader><DialogTitle className="font-heading">Delete repeating event</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-600">{del ? `"${del.title}" repeats ${del.recurrence === "custom" ? "on " : ""}${del._recurrence}. What would you like to remove?` : ""}</DialogDescription>
          <div className="flex flex-col gap-2 pt-2">
            <Button variant="outline" className="justify-start rounded-lg" onClick={() => doDeleteIds([del.id], "This date was removed from the series")} data-testid="skip-occurrence-btn">
              Skip only this date{del ? ` (${fmtDate(del.event_date)})` : ""}
            </Button>
            {upcomingIds.length > 1 && (
              <Button variant="outline" className="justify-start rounded-lg border-red-200 text-red-600 hover:bg-red-50" onClick={() => doDeleteIds(upcomingIds, "This and upcoming dates were removed")} data-testid="delete-upcoming-btn">
                Delete this and upcoming dates ({upcomingIds.length})
              </Button>
            )}
            <Button className="justify-start rounded-lg bg-red-600 hover:bg-red-700" onClick={() => doDeleteIds(del._ids, "Repeating series deleted")} data-testid="delete-series-btn">
              Delete entire series{del ? ` (${del._ids.length} occurrences)` : ""}
            </Button>
            <Button variant="ghost" className="rounded-lg" onClick={() => setDel(null)} data-testid="delete-recurring-cancel">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!del && !del._recurring} onOpenChange={(o) => !o && setDel(null)}
        title="Delete event?"
        description={del ? (del._multi ? `"${del.title}" spans multiple days — all ${del._ids.length} days will be removed from the schedule.` : `"${del.title}" will be removed from the schedule.`) : ""}
        confirmText="Delete" destructive onConfirm={doDelete} testId="delete-event-dialog"
      />
    </div>
  );
}

// Pending count for a tab badge (conflicts / swaps); refreshed when a queue changes.
function usePendingCount(enabled, url) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get(url).then(({ data }) => setCount(data.count || 0)).catch(() => {});
    load();
    window.addEventListener("rpg:badges-refresh", load);
    return () => window.removeEventListener("rpg:badges-refresh", load);
  }, [enabled, url]);
  return count;
}

export default function CoachSchedule() {
  const { schedule: scheduleOn, attendance: attendanceOn, conflicts: conflictsOn, volunteers: volunteersOn } = useFeatureFlags();
  const pending = usePendingCount(conflictsOn, "/conflicts/pending-count");
  const swapsPending = usePendingCount(volunteersOn, "/volunteers/swaps/pending-count");
  const tabs = [{ key: "calendar", label: "Calendar" }, attendanceOn && { key: "attendance", label: "Attendance" },
    conflictsOn && { key: "conflicts", label: "Conflicts", badge: pending }, volunteersOn && { key: "volunteers", label: "Volunteers", badge: swapsPending }].filter(Boolean);
  const [tab, setTab] = useTabParam(tabs.map((t) => t.key));
  if (!scheduleOn) return <FeatureDisabled name="Daily Schedule" />;
  return (
    <div data-testid="coach-schedule-page">
      <PageHeader title="Daily Schedule" />
      <ScheduleTabs tabs={tabs} value={tab} onChange={setTab} />
      {tab === "calendar" && <CalendarTab onVolunteer={() => setTab("volunteers")} />}
      {tab === "attendance" && <CoachAttendance />}
      {tab === "conflicts" && <CoachConflicts />}
      {tab === "volunteers" && <CoachVolunteers />}
    </div>
  );
}
